<template>
    <Dialog
        ref="dialogRef"
        v-model:visible="widgetModalState.opened"
        class="generated-link-modal"
        :class="dialogClass"
        :style="dialogStyle"
        append-to="#app"
        maximizable
        draggable
        :modal="!minimized"
        :dismissable-mask="false"
        :content-class="minimized ? '!h-0 !p-0' : 'grow'"
        :pt="{ header: { class: '!justify-end !py-2 !px-4' } }"
        data-testid="tl-dialog"
        @maximize="maximize"
        @unmaximize="unmaximize"
        @update:visible="handleVisibilityChange"
    >
        <template #header>
            <div class="w-full flex gap-4 pl-2">
                <div
                    v-if="widgetModalState.recordLabel || widgetModalState.recordId"
                    class="font-semibold"
                >{{ widgetModalState.recordLabel ?? widgetModalState.recordId }}
                </div>
            </div>
            <Button
                v-if="!dialogRef.maximized"
                id="minimize-trigger"
                type="reset"
                severity="secondary"
                text
                class="mr-[var(--p-dialog-header-gap)]"
                :icon="minimized
                    ? 'pi pi-arrow-up-right-and-arrow-down-left-from-center'
                    : 'pi pi-arrow-down-left-and-arrow-up-right-to-center'"
                :aria-label="'Close'"
                data-testid="minimize-modal"
                @click="minimizeToggle"
            />
        </template>

        <div
            v-show="!minimized"
            ref="widgetWrapper"
            class="widget-modal-wrapper"
        >
            <transactionlink-widget />
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import Dialog from 'primevue/dialog'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import useWidgetScript from '@/composables/widget/useWidgetScript'
import useWidgetModal from '@/composables/widget/useWidgetModal'
import { useEventBus, useMutationObserver } from '@vueuse/core'
import { useLockedRecords } from '@/modules/custom-table/composables/useLockedRecords'

const { widgetModalState, closeWidgetModal } = useWidgetModal()

const {
    unlockRecord,
    handleLockedRecordError,
} = useLockedRecords()

const widgetChangeIndicator = ref(false)

// Width the widget reports for its content; folded into the auto-size modal's min-width.
const contentDrivenMinWidth = ref<number | null>(null)

// Fallback overhead (px) — padding + border — used if the modal/iframe can't be measured.
const DEFAULT_MODAL_OVERHEAD = 50

// Deadband (px) for content-driven growth. At non-standard browser zoom the
// widget's clientWidth/scrollWidth round independently, yielding a phantom ~1px
// overflow that never resolves. Without a threshold each report grows the
// dialog, the iframe grows, the widget's ResizeObserver re-fires, and the modal
// grows in a loop. Only grow when the gain clears this rounding noise.
const GROWTH_TOLERANCE = 3

const hideModal = () => {
    closeWidgetModal()
}

let iframeStyleObserver: MutationObserver | undefined

// Watches for the widget script setting the iframe's height directly, which is how
// autoHeight is implemented. The first mutation marks the end of the pinned loading height.
const observeIframeHeight = () => {
    const iframeEl = widgetWrapper.value?.querySelector('iframe')
    if (!iframeEl || iframeStyleObserver) {
        return
    }

    iframeStyleObserver = new MutationObserver(() => {
        // Only a real height value marks the pin as safe to release — the widget
        // touches `style` for unrelated properties before it ever sets a height,
        // and releasing on those leaves the dialog un-pinned before the real
        // height lands, producing the same jump the pin exists to prevent.
        if (!iframeEl.style.height) {
            return
        }

        hasFirstContentResize.value = true
    })
    iframeStyleObserver.observe(iframeEl, { attributes: true, attributeFilter: ['style', 'height'] })
}

const { load, unload } = useWidgetScript(
    (api) => {
        api.setOptions({
            token: widgetModalState.value.token,
            inline: true,
            ...useAutoSize.value ? { autoHeight: true } : {},
        })
        api.open()

        api.on('workflow-finished', () => {
            unlockRecord(widgetModalState.value.recordId)
            hideModal()
        })

        api.on('view-context-changed', () => {
            useEventBus('widget-modal-view-context-changed').emit()

            contentDrivenMinWidth.value = null

            widgetChangeIndicator.value = true
            setTimeout(() => {
                widgetChangeIndicator.value = false
            }, 1000)
        })

        api.on('view-resized', (data?: { contentWidth?: number }) => {
            if (!data?.contentWidth) {
                return
            }
            // Skip while maximized — the content fills the maximized dialog, so its width would stick as min-width after restoring.
            if (dialogRef.value?.maximized) {
                return
            }

            // Convert iframe content width to dialog width.
            const dialogEl = getDialogElement()
            const iframeEl = widgetWrapper.value?.querySelector('iframe')
            const dialogWidth = dialogEl?.getBoundingClientRect().width
            const iframeWidth = iframeEl?.getBoundingClientRect().width
            const dialogOverhead = dialogEl && iframeEl
                ? Math.max(dialogWidth! - iframeWidth!, 0)
                : DEFAULT_MODAL_OVERHEAD

            const candidate = Math.ceil(data.contentWidth + dialogOverhead)

            // Ignore growth within rounding noise — otherwise subpixel jitter at
            // non-standard zoom ratchets the modal wider on every ResizeObserver tick.
            const currentWidth = Math.max(dialogWidth ?? 0, contentDrivenMinWidth.value ?? 0)
            if (candidate <= currentWidth + GROWTH_TOLERANCE) {
                return
            }

            contentDrivenMinWidth.value = candidate
        })

        api.on('error', (error) => {
            handleLockedRecordError(widgetModalState.value.recordId, error.message || 'Unknown error')
            hideModal()
        })
    },
)

const dialogRef = ref<ANY>()
const widgetWrapper = ref<HTMLElement>()

const minimized = ref(false)
const wasMinimized = ref(false)

const useAutoSize = computed(() => widgetModalState.value.workflowUiOptions?.widgetOptions?.crm?.autoSize)
const minWidth = computed(() => widgetModalState.value.workflowUiOptions?.widgetOptions?.crm?.minWidth)

const dialogClass = computed(() => {
    if (minimized.value) {
        return 'minimized w-full h-auto'
    }

    if (useAutoSize.value) {
        return 'w-auto h-auto'
    }

    return 'w-10/12 h-5/6'
})

// True once the widget has pushed its first real content-driven height.
// Before that, height is pinned via an explicit style (see dialogStyle) —
// min-height + h-auto proved unreliable at holding a floor across the async,
// cross-iframe reflow during the initial loading phase.
const hasFirstContentResize = ref(false)

// Matches the widget's initial loading-view iframe height (150px) plus dialog chrome.
const AUTO_SIZE_LOADING_HEIGHT = '220px'

// Fallback dialog width when auto-size is on but no minWidth is configured —
// otherwise the w-auto dialog collapses to the iframe's intrinsic ~300px.
// Matches the widget's native dialog-mode card width (--card-width: 420px).
const AUTO_SIZE_DEFAULT_MIN_WIDTH = '420px'

const dialogStyle = computed(() => {
    const transition = 'border 0.3s ease-in-out'

    if (!minimized.value) {
        // Auto-size dialog shrink-wraps the iframe (width:100%); min-width gives
        // it a definite width, else it collapses to the iframe's intrinsic
        // default. Cap via min(value, 98vw) — a floor, not max-width, which
        // can't beat a larger min-width and would block maximize.
        if (useAutoSize.value) {
            const base = minWidth.value || AUTO_SIZE_DEFAULT_MIN_WIDTH

            // max() keeps the configured/default minWidth as a floor; content can only widen the modal.
            const adjustedMinWidth = contentDrivenMinWidth.value
                ? `max(${base}, ${contentDrivenMinWidth.value}px)`
                : base

            return {
                transition,
                minWidth: `min(${adjustedMinWidth}, 98vw)`,
                ...!hasFirstContentResize.value ? { height: AUTO_SIZE_LOADING_HEIGHT } : {},
            }
        }

        return {
            transition,
            ...minWidth.value ? { minWidth: minWidth.value } : {},
        }
    }

    const border = widgetChangeIndicator.value
        ? '2px solid var(--tl-color-blue-600)'
        : '1px solid var(--tl-color-border)'

    return {
        transition,
        border,
    }
})

const maximize = () => {
    wasMinimized.value = minimized.value
    minimized.value = false
}

const unmaximize = () => {
    minimized.value = wasMinimized.value
}

const getDialogElement = () => {
    return document.querySelector('.generated-link-modal') as HTMLElement | undefined
}

const centerDialogWindow = () => {
    const dialogEl = getDialogElement()
    if (!dialogEl) {
        return
    }

    dialogEl.style.position = ''
    dialogEl.style.left = ''
    dialogEl.style.top = ''
}

const setMinimizedStyles = () => {
    const dialogEl = getDialogElement()
    if (!dialogEl) {
        return
    }

    dialogEl.style.position = 'fixed'
    dialogEl.style.top = `${window.innerHeight - 56}px`
    dialogEl.style.left = '0px'
}

const minimizeToggle = () => {
    if (dialogRef.value?.maximized) {
        dialogRef.value?.maximize()
    }

    minimized.value = !minimized.value

    nextTick(() => {
        if (minimized.value) {
            setMinimizedStyles()
        } else {
            centerDialogWindow()
        }
    })
}

const handleVisibilityChange = (isOpened: boolean) => {
    if (!isOpened) {
        unlockRecord(widgetModalState.value.recordId)
        closeWidgetModal()
    }
}

watch(
    [() => widgetModalState.value.opened, minimized],
    ([isOpened, isMinimized]) => {
        document.body.style.overflow = isOpened && !isMinimized ? 'hidden' : ''

        // Drop a previous run's measured width/height so the next workflow starts fresh.
        if (!isOpened) {
            contentDrivenMinWidth.value = null
            hasFirstContentResize.value = false
        }
    },
    { immediate: true },
)

// The widget embed builds the iframe after the dialog opens. The dialog owns
// scrolling, so force scrolling="no" to drop the iframe's own scrollbar. The
// observer catches a late-built iframe; the watch catches one already present.
// Also the first point where the iframe element exists, so it doubles as the
// trigger to attach the height observer used by the pinned loading height above.
const onIframeAvailable = () => {
    widgetWrapper.value?.querySelector('iframe')?.setAttribute('scrolling', 'no')
    observeIframeHeight()
}

useMutationObserver(widgetWrapper, onIframeAvailable, { childList: true, subtree: true })
watch(widgetWrapper, onIframeAvailable, { flush: 'post' })

onMounted(() => {
    load()
})

onUnmounted(() => {
    document.body.style.overflow = ''
    unload()
    iframeStyleObserver?.disconnect()
})
</script>

<style lang="sass" scoped>
.widget-modal-wrapper
    width: 100%
    height: 100%

#minimize-trigger
    flex-shrink: 0
    margin-left: 10px
</style>
